import React, { useEffect, useMemo, useRef, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { getListProductWithLocation } from '../../services/items.services';
import { countAction, CountDocument, getCountDocument, saveCountLines } from '../../services/count.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  Chip,
  ConfirmDialog,
  COUNT_STATUS,
  DocumentHeader,
  EmptyState,
  ErrorBanner,
  Fact,
  FieldMessage,
  IconButton,
  LinesCard,
  MenuItem,
  MUTED,
  NextStep,
  NumberField,
  PageFrame,
  PhoneLabel,
  PhoneLine,
  PickerRow,
  ProductCell,
  ProductPicker,
  ReadValue,
  SaveBar,
  SelectField,
  Skeleton,
  TEXT,
  TotalsList,
  card,
  countStatusOf,
  defaultUnitId,
  documentDateTime,
  quantity,
  signedMoney,
  tableStyle,
  td,
  th,
  unitsOfProduct,
  useLanguage,
} from '../../components/purchasing';
import { COUNT_KINDS } from './NewCountDialog';
import {
  CountChange,
  CountLine,
  approvalChanges,
  baseName,
  checkQty,
  countingChanges,
  lineOfDocument,
  lineOfPick,
  linesPayload,
  unitName,
  valueTotals,
  varianceOf,
  varianceValue,
} from './countLines';

const PICK_PAGE = 20;
const COUNTING: number[] = [COUNT_STATUS.Draft, COUNT_STATUS.RecountRequested];
/** Statuses whose lines carry the stock the count was sent with. */
const SENT: number[] = [COUNT_STATUS.SentForApproval, COUNT_STATUS.Completed, COUNT_STATUS.Rejected];

type Mode = 'count' | 'approve' | 'read';
type Busy = '' | 'save' | 'send' | 'approve' | 'recount' | 'reject' | 'cancel';
type Confirm = '' | 'send' | 'approve' | 'recount' | 'reject' | 'cancel' | 'leave';

const personName = (person: { name: string | null } | null | undefined) => person?.name || null;

/** "+2", "−22" or "0"; the base unit follows when the line is counted in another unit. */
const signedQty = (value: number | null, unit: string) => {
  if (value === null) return '—';
  const text = value > 0 ? `+${quantity(value)}` : value < 0 ? `−${quantity(-value)}` : '0';
  return unit ? `${text} ${unit}` : text;
};

/** One page for a count in every state: counted and sent here, then approved, sent back or rejected by an approver. */
const CountDetail = () => {
  const preLanguage = localStorage.getItem('language');
  const id: string = window.location.pathname.split('/').pop() || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [doc, setDoc] = useState<CountDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [lines, setLines] = useState<CountLine[]>([]);
  const [saved, setSaved] = useState<CountLine[]>([]);
  const [notice, setNotice] = useState('');
  const [lineSearch, setLineSearch] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [busy, setBusy] = useState<Busy>('');
  const [confirm, setConfirm] = useState<Confirm>('');

  // Add products
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [pickTerm, setPickTerm] = useState('');
  const [picks, setPicks] = useState<any[]>([]);
  const [pickCount, setPickCount] = useState(0);
  const [pickPage, setPickPage] = useState(1);
  const [picksLoading, setPicksLoading] = useState(false);
  const [selected, setSelected] = useState<Map<string, string>>(() => new Map());
  const pickSeq = useRef(0);

  const count = doc?.count;
  const statusId = Number(count?.statusId);
  const siteId = count?.location?.id ?? '';
  const siteName = count?.location?.name ?? '';
  const canApprove = !!doc?.viewer?.canApprove;
  const mode: Mode = !count ? 'read' : COUNTING.includes(statusId) ? 'count' : statusId === COUNT_STATUS.SentForApproval && canApprove ? 'approve' : 'read';
  const counting = mode === 'count';
  const approving = mode === 'approve';
  const sent = SENT.includes(statusId);
  const title = count ? t('Count #{number}', { number: count.internalNumber }) : t('Count');
  const number = `#${count?.internalNumber ?? ''}`;

  /* ---------------- Loading ---------------- */

  const apply = (next: CountDocument | null) => {
    const list = (next?.lines ?? []).map(lineOfDocument);
    setDoc(next);
    setLines(list);
    setSaved(list);
    setNotice('');
  };

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      apply((await getCountDocument(id))?.data?.data ?? null);
    } catch (error: any) {
      setFailed(true);
      showApiError(error, { language });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id === 'new') {
      // Counts are started from the list's New count dialog.
      webNavigate(`/${preLanguage}/stockCounts?new=1`);
      return;
    }
    load();
    const onToken = () => load();
    document.addEventListener('newToken', onToken);
    return () => document.removeEventListener('newToken', onToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ---------------- Lines ---------------- */

  // The figure each mode edits: counted while counting, approved while approving.
  const typedOf = (line: CountLine) => (counting ? line.counted : line.approved);
  const checks = useMemo(() => new Map(lines.map((line) => [line.key, checkQty(counting ? line.counted : line.approved)])), [lines, counting]);
  const changes: CountChange[] = counting ? countingChanges(lines, saved) : approving ? approvalChanges(lines, saved) : [];
  const dirty = changes.length > 0;
  const attention = lines.filter((line) => checks.get(line.key)?.error).length;
  const countedLines = lines.filter((line) => checkQty(line.counted).qty !== null);
  const variance = (line: CountLine) => (sent ? varianceOf(line, line.approved) : null);
  const withDifference = sent ? lines.filter((line) => (variance(line) ?? 0) !== 0) : [];

  const term = lineSearch.trim().toLowerCase();
  const narrowed = onlyOpen ? (counting ? lines.filter((line) => checkQty(line.counted).qty === null) : withDifference) : lines;
  const shown = term ? narrowed.filter((line) => `${line.name} ${line.sku}`.toLowerCase().includes(term)) : narrowed;

  const patch = (key: string, change: Partial<CountLine>) => {
    setNotice('');
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...change } : line)));
  };
  const removeLine = (key: string) => setLines((prev) => prev.filter((line) => line.key !== key));
  const discard = () => {
    setLines(saved);
    setNotice('');
  };

  /** Shows every line's problem and moves to the first one, which may be far below the header. */
  const flagAndFocus = () => {
    setOnlyOpen(false);
    setLineSearch('');
    window.setTimeout(() => {
      const first = document.querySelector<HTMLInputElement>('[data-lines] input[aria-invalid="true"]');
      first?.scrollIntoView({ block: 'center' });
      first?.focus();
    }, 0);
  };

  /* ---------------- Actions ---------------- */

  const backToList = () => webNavigate(`/${preLanguage}/stockCounts`);
  const onBack = () => (dirty ? setConfirm('leave') : backToList());

  /** Saves what changed on screen; the API answers with the count as it now is. */
  const persist = async (): Promise<CountDocument | null> => {
    if (!dirty) return doc;
    const response: any = await saveCountLines(id, linesPayload(lines, saved, counting ? 'counting' : 'approval'));
    return response?.data?.data ?? null;
  };
  /** Saves first, so an action that then fails leaves the page as saved. */
  const persistThen = async (action: 'send' | 'approve') => {
    if (dirty) apply(await persist());
    return (await countAction(id, action))?.data?.data ?? null;
  };

  const run = async (kind: Busy, work: () => Promise<CountDocument | null>, done: string) => {
    if (busy) return;
    setBusy(kind);
    try {
      const next = await work();
      setConfirm('');
      apply(next);
      showToast({ message: done, type: 'success', duration: 6000 });
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const save = () => {
    if (attention) return flagAndFocus();
    run('save', persist, approving ? t('Approved figures saved') : t('Count saved'));
  };

  const startSend = () => {
    if (attention) return flagAndFocus();
    if (countedLines.length === 0) {
      setNotice(t('Count at least one product before sending.'));
      return;
    }
    setConfirm('send');
  };
  const send = () => run('send', () => persistThen('send'), t('Count sent for approval'));

  const startApprove = () => {
    if (attention) return flagAndFocus();
    setConfirm('approve');
  };
  const approve = () => run('approve', () => persistThen('approve'), t('Count approved. Stock at {site} is corrected.', { site: siteName }));

  // Sending back or closing drops approved figures not saved yet.
  const decide = (action: 'recount' | 'reject' | 'cancel', done: string) => run(action, async () => (await countAction(id, action))?.data?.data ?? null, done);

  /* ---------------- Add products ---------------- */

  useDebounceEffect(() => setPickTerm(pickSearch.trim()), [pickSearch], 300);

  const loadPicks = async (page: number, append: boolean) => {
    const mine = ++pickSeq.current;
    setPicksLoading(true);
    try {
      // The site's products, with its stock.
      const response: any = await getListProductWithLocation(page, PICK_PAGE, pickTerm, siteId);
      if (mine !== pickSeq.current) return;
      const rows: any[] = response?.data?.productWithCompany ?? [];
      setPicks((prev) => {
        if (!append) return rows;
        const known = new Set(prev.map((row) => String(row.id)));
        return [...prev, ...rows.filter((row) => !known.has(String(row.id)))];
      });
      setPickCount(Number(response?.data?.count) || 0);
      setPickPage(page);
    } catch (error: any) {
      if (mine === pickSeq.current) showApiError(error, { language });
    } finally {
      if (mine === pickSeq.current) setPicksLoading(false);
    }
  };

  useEffect(() => {
    if (pickerOpen) loadPicks(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, pickTerm]);

  const openPicker = () => {
    setPickSearch('');
    setPickTerm('');
    setSelected(new Map());
    setPickerOpen(true);
  };

  const onCount = new Set(lines.map((line) => line.productId));
  const pickerRows: PickerRow[] = picks.map((pick) => {
    const productId = String(pick.id);
    const units = unitsOfProduct(pick);
    const base = units.find((unit) => unit.isBase)?.name ?? '';
    const stock = Number(pick?.currentStock);
    return {
      id: productId,
      name: pick?.productName ?? '',
      sku: pick?.sku ?? null,
      group: [pick?.productGroup?.groupName, Number.isFinite(stock) ? t('On hand {qty}', { qty: `${quantity(stock)}${base ? ` ${base}` : ''}` }) : null].filter(Boolean).join(' · '),
      units: units.map((unit) => ({ id: unit.id, name: unit.name, taken: onCount.has(productId) })),
      selected: selected.has(productId),
      unitId: selected.get(productId) ?? defaultUnitId(units),
    };
  });

  const togglePick = (row: PickerRow) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else if (row.unitId) next.set(row.id, row.unitId);
      return next;
    });
  const pickUnit = (row: PickerRow, unitId: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(row.id, unitId);
      return next;
    });

  const addPicked = () => {
    const known = new Set(lines.map((line) => line.productId));
    const added: CountLine[] = [];
    selected.forEach((unitId, productId) => {
      const pick = picks.find((row) => String(row.id) === productId);
      if (!pick || !unitId || known.has(productId)) return;
      known.add(productId);
      // A product removed on screen and picked again comes back as its saved line.
      const before = saved.find((line) => line.productId === productId);
      added.push(before ? { ...before, productUOMId: unitId } : lineOfPick(pick, unitId));
    });
    setLines((prev) => [...prev, ...added]);
    // A just-added line is never hidden by the line search or the filter.
    setLineSearch('');
    setOnlyOpen(false);
    setPickerOpen(false);
  };

  /* ---------------- Header ---------------- */

  const kindLabel = t(COUNT_KINDS.find((entry) => entry.type === Number(count?.type))?.label ?? 'All products');
  let decision: Pick<Fact, 'value' | 'pending' | 'lines'>;
  if (statusId === COUNT_STATUS.Completed) decision = { value: documentDateTime(count?.approvedAt), lines: [personName(count?.approvedBy)].filter(Boolean) as string[] };
  else if (statusId === COUNT_STATUS.Rejected)
    decision = { value: t('Rejected'), lines: [personName(count?.approvedBy), count?.approvedAt ? documentDateTime(count.approvedAt) : null].filter(Boolean) as string[] };
  else if (statusId === COUNT_STATUS.Cancelled) decision = { pending: t('Cancelled') };
  else if (statusId === COUNT_STATUS.SentForApproval) decision = { pending: t('Waiting for approval') };
  else decision = { pending: t('Not sent yet') };
  const facts: Fact[] = count
    ? [
        { key: 'site', label: t('Site'), value: siteName, pending: '—' },
        { key: 'kind', label: t('What is counted'), value: kindLabel },
        { key: 'reason', label: t('Reason'), value: count.reason?.name, pending: t('No reason') },
        { key: 'created', label: t('Created'), value: documentDateTime(count.createdAt), lines: [personName(count.createdBy)].filter(Boolean) as string[], newRow: true, numeric: true },
        { key: 'approved', label: t('Approved'), numeric: true, ...decision },
      ]
    : [];

  const approveButton = (
    <Button variant="primary" phone={phone} fullWidth={phone} disabled={!!busy} onClick={startApprove}>
      {t('Approve count')}
    </Button>
  );
  let nextStep: NextStep | null = null;
  if (statusId === COUNT_STATUS.RecountRequested) {
    nextStep = { tone: 'progress', text: t('An approver sent this count back. Count again, then send it for approval.') };
  } else if (statusId === COUNT_STATUS.SentForApproval) {
    nextStep = approving
      ? {
          tone: 'waiting',
          text: t('Check the approved figures. Approving corrects the stock at {site} by the differences.', { site: siteName }),
          actions: phone ? (
            approveButton
          ) : (
            <>
              <Button variant="danger" disabled={!!busy} onClick={() => setConfirm('reject')}>
                {t('Reject count')}
              </Button>
              <Button disabled={!!busy} onClick={() => setConfirm('recount')}>
                {t('Ask for a recount')}
              </Button>
              {approveButton}
            </>
          ),
        }
      : { tone: 'waiting', text: t('Waiting for someone with the Stock Count Approval permission.') };
  } else if (statusId === COUNT_STATUS.Rejected) {
    nextStep = { tone: 'stopped', text: t('This count was rejected. No stock changed.') };
  } else if (statusId === COUNT_STATUS.Cancelled) {
    nextStep = { tone: 'stopped', text: t('This count was cancelled. No stock changed.') };
  }

  // A sent count is with the approvers: its counter or an approver may withdraw it.
  const canCancel = counting || (statusId === COUNT_STATUS.SentForApproval && (canApprove || !!doc?.viewer?.isCreator));
  const moreItems: MenuItem[] = [
    ...(phone && dirty ? [{ key: 'discard', label: t('Discard changes'), icon: 'undo' as const, onSelect: discard }] : []),
    ...(approving && phone
      ? [
          { key: 'recount', label: t('Ask for a recount'), icon: 'undo' as const, onSelect: () => setConfirm('recount') },
          { key: 'reject', label: t('Reject count'), icon: 'close' as const, danger: true, onSelect: () => setConfirm('reject') },
        ]
      : []),
    ...(canCancel ? [{ key: 'cancel', label: t('Cancel count'), icon: 'close' as const, danger: true, onSelect: () => setConfirm('cancel') }] : []),
  ];

  /* ---------------- Lines card ---------------- */

  // Expected and the difference are base units; the unit is named when the line counts in another one.
  const baseSuffix = (line: CountLine) => (line.productUOMId && line.units.find((unit) => unit.id === line.productUOMId)?.isBase ? '' : baseName(line));
  const onHandText = (line: CountLine) => t('On hand {qty}', { qty: `${quantity(line.onHand)}${baseName(line) ? ` ${baseName(line)}` : ''}` });
  const contextOf = (line: CountLine) => (counting ? onHandText(line) : line.group);

  const messageOf = (line: CountLine) => {
    const check = checks.get(line.key);
    if (check?.error) return t(check.error);
    return '';
  };

  const qtyInput = (line: CountLine, inputId: string) => {
    const error = messageOf(line);
    const column = counting ? 'counted' : 'approved';
    return (
      <>
        <NumberField
          id={inputId}
          column={column}
          phone={phone}
          srLabel={phone ? undefined : counting ? t('Counted for {product}', { product: line.name }) : t('Approved for {product}', { product: line.name })}
          value={typedOf(line)}
          invalid={!!error}
          describedBy={error ? `${inputId}-msg` : undefined}
          onChange={(value) => patch(line.key, counting ? { counted: value } : { approved: value })}
        />
        {error && (
          <FieldMessage id={`${inputId}-msg`} tone="error">
            {error}
          </FieldMessage>
        )}
      </>
    );
  };

  const unitSelect = (line: CountLine, selectId: string) => (
    <SelectField id={selectId} phone={phone} value={line.productUOMId} onChange={(event) => patch(line.key, { productUOMId: event.target.value })} wrapperStyle={phone ? { flex: 'none', width: '116px' } : undefined}>
      {!line.productUOMId && <option value="">{t('Choose a unit')}</option>}
      {line.units.map((unit) => (
        <option key={unit.id} value={unit.id}>
          {unit.name}
        </option>
      ))}
    </SelectField>
  );

  const countedText = (line: CountLine) => {
    const qty = checkQty(line.counted).qty;
    return qty === null ? '—' : quantity(qty);
  };
  const approvedText = (line: CountLine) => {
    const qty = checkQty(line.approved).qty;
    return qty === null ? '—' : quantity(qty);
  };
  const valueOf = (line: CountLine) => varianceValue(line, variance(line));

  const desktopTable = () => {
    if (counting) {
      return (
        <div data-lines="" style={{ overflowX: 'auto' }}>
          <table style={tableStyle(600)}>
            <thead>
              <tr>
                <th scope="col" style={th('left', { first: true })}>
                  {t('Product')}
                </th>
                <th scope="col" style={th('left', { width: 124 })}>
                  {t('Unit')}
                </th>
                <th scope="col" style={th('right', { width: 120, padRight: 0 })}>
                  {t('Counted')}
                </th>
                <th scope="col" style={th('left', { width: 44, padLeft: 2, padRight: 10 })}>
                  <span className="pu-sr">{t('Remove')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((line) => {
                const error = !!checks.get(line.key)?.error;
                return (
                  <tr key={line.key} className="pu-row">
                    <td style={td({ first: true, error })}>
                      <ProductCell name={line.name} sku={line.sku} context={contextOf(line)} />
                    </td>
                    <td style={td({ error })}>
                      <label className="pu-sr" htmlFor={`unit-${line.key}`}>
                        {t('Unit for {product}', { product: line.name })}
                      </label>
                      {unitSelect(line, `unit-${line.key}`)}
                    </td>
                    <td style={td({ align: 'right', padRight: 0, error })}>{qtyInput(line, `qty-${line.key}`)}</td>
                    <td style={td({ align: 'right', padLeft: 2, padRight: 10, padTop: 16, error })}>
                      <IconButton icon="close" remove label={t('Remove {product}', { product: line.name })} onClick={() => removeLine(line.key)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    if (!sent) {
      return (
        <div data-lines="" style={{ overflowX: 'auto' }}>
          <table style={tableStyle(560)}>
            <thead>
              <tr>
                <th scope="col" style={th('left', { first: true })}>
                  {t('Product')}
                </th>
                <th scope="col" style={th('left', { width: 124 })}>
                  {t('Unit')}
                </th>
                <th scope="col" style={th('right', { width: 110, last: true })}>
                  {t('Counted')}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((line) => (
                <tr key={line.key} className="pu-row">
                  <td style={td({ first: true })}>
                    <ProductCell name={line.name} sku={line.sku} context={line.group} />
                  </td>
                  <td style={td()}>
                    <ReadValue>{unitName(line) || '—'}</ReadValue>
                  </td>
                  <td style={td({ align: 'right', last: true })}>
                    <ReadValue muted={checkQty(line.counted).qty === null}>{countedText(line)}</ReadValue>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <div data-lines="" style={{ overflowX: 'auto' }}>
        <table style={tableStyle(880)}>
          <thead>
            <tr>
              <th scope="col" style={th('left', { first: true })}>
                {t('Product')}
              </th>
              <th scope="col" style={th('left', { width: 104 })}>
                {t('Unit')}
              </th>
              <th scope="col" style={th('right', { width: 88 })}>
                {t('Counted')}
              </th>
              <th scope="col" style={th('right', { width: approving ? 124 : 96 })}>
                {t('Approved')}
              </th>
              <th scope="col" style={th('right', { width: 120 })}>
                {t('Expected')}
              </th>
              <th scope="col" style={th('right', { width: 120 })}>
                {t('Difference')}
              </th>
              <th scope="col" style={th('right', { width: 104, last: true })}>
                {t('Value')}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((line) => {
              const error = approving && !!checks.get(line.key)?.error;
              const diff = variance(line);
              const value = valueOf(line);
              const suffix = baseSuffix(line);
              return (
                <tr key={line.key} className="pu-row">
                  <td style={td({ first: true, error })}>
                    <ProductCell name={line.name} sku={line.sku} context={contextOf(line)} />
                  </td>
                  <td style={td({ error })}>
                    <ReadValue>{unitName(line) || '—'}</ReadValue>
                  </td>
                  <td style={td({ align: 'right', error })}>
                    <ReadValue muted={checkQty(line.counted).qty === null}>{countedText(line)}</ReadValue>
                  </td>
                  <td style={td({ align: 'right', error })}>
                    {approving ? qtyInput(line, `qty-${line.key}`) : <ReadValue muted={checkQty(line.approved).qty === null}>{approvedText(line)}</ReadValue>}
                  </td>
                  <td style={td({ align: 'right', error })}>
                    <ReadValue>{line.expectedQty === null ? '—' : `${quantity(line.expectedQty)}${suffix ? ` ${suffix}` : ''}`}</ReadValue>
                  </td>
                  <td style={td({ align: 'right', error })}>
                    <ReadValue strong={!!diff} muted={diff === null}>
                      {signedQty(diff, suffix)}
                    </ReadValue>
                  </td>
                  <td style={td({ align: 'right', last: true, error })}>
                    <ReadValue strong={!!value} muted={value === null}>
                      {value === null ? '—' : signedMoney(value)}
                    </ReadValue>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const phoneLines = () => (
    <div data-lines="">
      {shown.map((line) => {
        const nameId = `line-${line.key}`;
        const error = (counting || approving) && !!checks.get(line.key)?.error;
        if (counting) {
          return (
            <PhoneLine key={line.key} labelledBy={nameId} error={error}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <ProductCell phone nameId={nameId} name={line.name} sku={line.sku} context={line.group} />
                <label className="pu-sr" htmlFor={`m-unit-${line.key}`}>
                  {t('Unit for {product}', { product: line.name })}
                </label>
                {unitSelect(line, `m-unit-${line.key}`)}
                <IconButton icon="close" remove phone label={t('Remove {product}', { product: line.name })} onClick={() => removeLine(line.key)} style={{ marginRight: '-16px' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginTop: '10px' }}>
                <div style={{ minWidth: 0 }}>
                  <PhoneLabel htmlFor={`m-qty-${line.key}`}>{t('Counted')}</PhoneLabel>
                  {qtyInput(line, `m-qty-${line.key}`)}
                </div>
                <div style={{ minWidth: 0, textAlign: 'right' }}>
                  <PhoneLabel align="right">{t('On hand')}</PhoneLabel>
                  <ReadValue phone>{`${quantity(line.onHand)}${baseName(line) ? ` ${baseName(line)}` : ''}`}</ReadValue>
                </div>
              </div>
            </PhoneLine>
          );
        }
        const diff = variance(line);
        const value = valueOf(line);
        const suffix = baseSuffix(line);
        return (
          <PhoneLine key={line.key} labelledBy={nameId} error={error}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <ProductCell
                phone
                nameId={nameId}
                name={line.name}
                sku={line.sku}
                context={sent ? t('Counted {qty}', { qty: `${countedText(line)} ${unitName(line)}`.trim() }) : line.group}
              />
              <p style={{ flex: 'none', fontSize: '15px', lineHeight: '22px', color: TEXT }}>
                <span className="pu-sr">{t('Unit')}: </span>
                {unitName(line)}
              </p>
            </div>
            {sent ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px', textAlign: 'right' }}>
                <div style={{ minWidth: 0 }}>
                  {approving ? (
                    <>
                      <PhoneLabel htmlFor={`m-qty-${line.key}`} align="right">
                        {t('Approved')}
                      </PhoneLabel>
                      {qtyInput(line, `m-qty-${line.key}`)}
                    </>
                  ) : (
                    <>
                      <PhoneLabel align="right">{t('Approved')}</PhoneLabel>
                      <ReadValue phone muted={checkQty(line.approved).qty === null}>
                        {approvedText(line)}
                      </ReadValue>
                    </>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <PhoneLabel align="right">{t('Expected')}</PhoneLabel>
                  <ReadValue phone>{line.expectedQty === null ? '—' : `${quantity(line.expectedQty)}${suffix ? ` ${suffix}` : ''}`}</ReadValue>
                </div>
                <div style={{ minWidth: 0 }}>
                  <PhoneLabel align="right">{t('Difference')}</PhoneLabel>
                  <ReadValue phone strong={!!diff} muted={diff === null}>
                    {signedQty(diff, suffix)}
                  </ReadValue>
                  {value !== null && value !== 0 && <p style={{ fontSize: '12.5px', lineHeight: '18px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{signedMoney(value)}</p>}
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '10px', textAlign: 'right' }}>
                <PhoneLabel align="right">{t('Counted')}</PhoneLabel>
                <ReadValue phone muted={checkQty(line.counted).qty === null}>
                  {countedText(line)}
                </ReadValue>
              </div>
            )}
          </PhoneLine>
        );
      })}
    </div>
  );

  const linesBody = () => {
    if (loading && !doc) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: phone ? '16px' : '20px' }}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      );
    }
    if (lines.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          {counting ? (
            <EmptyState
              phone={phone}
              title={t('No products on this count yet')}
              desc={t('Use Add products to choose what you count at {site}.', { site: siteName })}
              action={
                <Button variant="primary" phone={phone} icon="plus" onClick={openPicker}>
                  {t('Add products')}
                </Button>
              }
            />
          ) : (
            <EmptyState phone={phone} title={t('No products on this count')} />
          )}
        </div>
      );
    }
    if (shown.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          {term ? (
            <EmptyState
              icon="search"
              phone={phone}
              title={t('No products on this count match "{search}". Check the spelling or search by SKU.', { search: lineSearch.trim() })}
              action={
                <Button variant="text" phone={phone} onClick={() => setLineSearch('')}>
                  {t('Clear search')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              phone={phone}
              title={counting ? t('Every product on this count is counted') : t('No product differs from the stock it was sent with')}
              action={
                <Button variant="text" phone={phone} onClick={() => setOnlyOpen(false)}>
                  {t('Show all products')}
                </Button>
              }
            />
          )}
        </div>
      );
    }
    return phone ? phoneLines() : desktopTable();
  };

  /* ---------------- Totals and save bar ---------------- */

  const openCount = lines.length - countedLines.length;
  const totals = valueTotals(lines, (line) => line.approved);
  const totalRows = sent
    ? [
        { label: t('Stock up'), value: signedMoney(totals.up) },
        { label: t('Stock down'), value: signedMoney(totals.down) },
        { label: statusId === COUNT_STATUS.Completed ? t('Stock corrected by') : t('Stock changes by'), value: signedMoney(totals.net), strong: true },
      ]
    : [{ label: t('Counted'), value: t('{counted} of {lines}', { counted: countedLines.length, lines: lines.length }), strong: true }];

  const filterChip =
    lines.length > 0 && (counting || sent) ? (
      <Chip pressed={onlyOpen} phone={phone} onClick={() => setOnlyOpen((prev) => !prev)}>
        {counting ? t('Not counted · {count}', { count: openCount }) : t('With a difference · {count}', { count: withDifference.length })}
      </Chip>
    ) : null;

  const changeText = (change: CountChange) => {
    const line = (change.kind === 'removed' ? saved : lines).find((other) => other.key === change.key);
    const name = line?.name ?? '';
    return change.kind === 'added' ? t('Added {name}', { name }) : change.kind === 'removed' ? t('Removed {name}', { name }) : t('Edited {name}', { name });
  };
  const attentionText = attention ? (attention === 1 ? t('1 line needs attention') : t('{count} lines need attention', { count: attention })) : null;
  const progress = t('{counted} of {lines} counted', { counted: countedLines.length, lines: lines.length });

  const countBar = counting && doc && (
    <SaveBar
      phone={phone}
      label={t('Unsaved changes')}
      summary={{
        lead: dirty ? (changes.length === 1 ? t('1 unsaved change') : t('{count} unsaved changes', { count: changes.length })) : progress,
        detail: dirty ? [progress] : [],
        attention: notice || attentionText,
        quiet: !dirty,
      }}
      secondary={
        phone ? (
          <Button variant="text" phone disabled={!dirty || !!busy} onClick={save}>
            {busy === 'save' ? t('Saving…') : t('Save count')}
          </Button>
        ) : (
          <>
            <Button variant="text" disabled={!dirty || !!busy} onClick={discard}>
              {t('Discard changes')}
            </Button>
            <Button disabled={!dirty || !!busy} aria-busy={busy === 'save' || undefined} onClick={save}>
              {busy === 'save' ? t('Saving…') : t('Save count')}
            </Button>
          </>
        )
      }
      primary={
        <Button variant="primary" phone={phone} fullWidth={phone} disabled={lines.length === 0 || !!busy} aria-busy={busy === 'send' || undefined} onClick={startSend}>
          {busy === 'send' ? t('Sending…') : t('Send for approval')}
        </Button>
      }
    />
  );

  // Once an approved figure changes, the bar keeps the save and the Approve button in reach under long lists.
  const approveBar = approving && dirty && (
    <SaveBar
      phone={phone}
      label={t('Unsaved changes')}
      summary={{
        lead: changes.length === 1 ? t('1 unsaved change') : t('{count} unsaved changes', { count: changes.length }),
        detail: [changes.length > 1 ? t('{first}, +{count} more', { first: changeText(changes[0]), count: changes.length - 1 }) : changeText(changes[0])],
        attention: attentionText,
      }}
      secondary={
        phone ? (
          <Button variant="text" phone disabled={!!busy} onClick={save}>
            {busy === 'save' ? t('Saving…') : t('Save')}
          </Button>
        ) : (
          <>
            <Button variant="text" disabled={!!busy} onClick={discard}>
              {t('Discard changes')}
            </Button>
            <Button disabled={!!busy} aria-busy={busy === 'save' || undefined} onClick={save}>
              {busy === 'save' ? t('Saving…') : t('Save')}
            </Button>
          </>
        )
      }
      primary={approveButton}
    />
  );

  const upCount = lines.filter((line) => (variance(line) ?? 0) > 0).length;
  const downCount = lines.filter((line) => (variance(line) ?? 0) < 0).length;

  return (
    <>
      <PageFrame phone={phone} withPhoneBar={counting || (approving && dirty)}>
        {failed && !doc ? (
          <ErrorBanner message={t('The count could not be loaded.')} retryLabel={t('Try again')} onRetry={load} />
        ) : (
          <>
            {doc ? (
              <DocumentHeader phone={phone} t={t} backLabel={t('Back to counts')} onBack={onBack} title={title} status={countStatusOf(statusId)} nextStep={nextStep} facts={facts} moreItems={moreItems} />
            ) : (
              <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', padding: phone ? '16px' : '20px' }}>
                <Skeleton width="120px" />
                <Skeleton width="220px" height={28} />
                <Skeleton height={96} />
              </section>
            )}
            <LinesCard
              phone={phone}
              titleId="pu-lines-title"
              title={t('Products')}
              count={doc ? lines.length : undefined}
              search={lines.length > 0 ? { label: t('Find a product in this count'), placeholder: t('Find a product in this count'), value: lineSearch, onChange: setLineSearch } : undefined}
              actions={
                filterChip || (counting && lines.length > 0) ? (
                  <>
                    {filterChip}
                    {counting && lines.length > 0 && (
                      <Button phone={phone} icon="plus" onClick={openPicker}>
                        {t('Add products')}
                      </Button>
                    )}
                  </>
                ) : undefined
              }
              footer={lines.length > 0 && <TotalsList phone={phone} rows={totalRows} />}
            >
              {linesBody()}
            </LinesCard>
            {countBar}
            {approveBar}
          </>
        )}
      </PageFrame>
      {counting && (
        <ProductPicker
          open={pickerOpen}
          phone={phone}
          t={t}
          title={t('Add products to {count}', { count: title })}
          search={pickSearch}
          onSearch={setPickSearch}
          groups={[]}
          groupId=""
          onGroup={() => undefined}
          rows={pickerRows}
          loading={picksLoading}
          hasMore={picks.length < pickCount}
          onMore={() => loadPicks(pickPage + 1, true)}
          onToggle={togglePick}
          onUnit={pickUnit}
          selectedCount={selected.size}
          onCancel={() => setPickerOpen(false)}
          onAdd={addPicked}
          takenLabel={t('On this count')}
          unitLabel={(product) => t('Unit for {product}', { product })}
          searchPlaceholder={t('Name or SKU')}
        />
      )}
      <ConfirmDialog
        open={confirm === 'send'}
        phone={phone}
        busy={busy === 'send'}
        title={t('Send count {number} for approval?', { number })}
        body={
          <>
            {t('{counted} of {lines} products are counted.', { counted: countedLines.length, lines: lines.length })}{' '}
            {openCount > 0 && t('Products left empty are not counted, and their stock does not change.')}{' '}
            {t('The stock at {site} now is kept with the count, so sales after this do not count as differences.', { site: siteName })}
          </>
        }
        confirmLabel={t('Send for approval')}
        cancelLabel={t('Keep counting')}
        closeLabel={t('Close')}
        onConfirm={send}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'approve'}
        phone={phone}
        busy={busy === 'approve'}
        title={t('Approve count {number}?', { number })}
        body={
          <>
            {t('Stock at {site} changes by {amount}: {up} products up, {down} down.', { site: siteName, amount: signedMoney(totals.net), up: upCount, down: downCount })}{' '}
            {t('Products without an approved figure do not change.')} {t('This cannot be undone.')}
          </>
        }
        confirmLabel={t('Approve count')}
        cancelLabel={t('Keep checking')}
        closeLabel={t('Close')}
        onConfirm={approve}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'recount'}
        phone={phone}
        busy={busy === 'recount'}
        title={t('Ask for a recount of {number}?', { number })}
        body={t('The count goes back to counting at {site}, and the approved figures are cleared. No stock changes.', { site: siteName })}
        confirmLabel={t('Ask for a recount')}
        cancelLabel={t('Cancel')}
        closeLabel={t('Close')}
        onConfirm={() => decide('recount', t('Count sent back for a recount'))}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'reject'}
        phone={phone}
        danger
        busy={busy === 'reject'}
        title={t('Reject count {number}?', { number })}
        body={t('The count is marked rejected and no stock changes at {site}. This cannot be undone.', { site: siteName })}
        confirmLabel={t('Reject count')}
        cancelLabel={t('Cancel')}
        closeLabel={t('Close')}
        onConfirm={() => decide('reject', t('Count rejected'))}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        phone={phone}
        danger
        busy={busy === 'cancel'}
        title={t('Cancel count {number}?', { number })}
        body={t('The count is marked cancelled and no stock changes. This cannot be undone.')}
        confirmLabel={t('Cancel count')}
        cancelLabel={t('Keep count')}
        closeLabel={t('Close')}
        onConfirm={() => decide('cancel', t('Count cancelled'))}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'leave'}
        phone={phone}
        danger
        title={t('Leave without saving?')}
        body={approving ? t('The approved figures you changed are not saved.') : t('The quantities you counted are not saved.')}
        confirmLabel={t('Leave without saving')}
        cancelLabel={t('Keep editing')}
        closeLabel={t('Close')}
        onConfirm={() => {
          setConfirm('');
          backToList();
        }}
        onCancel={() => setConfirm('')}
      />
    </>
  );
};

export default CountDetail;
